// Text color resolution for feed posts with a background (article.background /
// article.backgroundImage). Rendered by components/gui/listFrontpage.js.
//
// article.background is a bare hex color from COLOR_PICKER_LIST (an optional
// ",foreground" suffix is supported but the pickers never produce one), while
// article.backgroundImage values embed their foreground after ";". Without an
// explicit foreground the text used to inherit the theme body color, which is
// invisible on a same-luminance background (black post in light mode, light
// post in dark mode) — so bare hex backgrounds get a contrast color instead,
// mirroring the composer preview in components/form/input.js (isDark → white).
import util from './util';
import { isGradientValue, splitBackgroundValue } from './fieldsHelpers';

// Re-exported for existing consumers (components/article/edit.js, tests); the
// helpers live in fieldsHelpers.js next to the picker lists that produce the
// values, so shipped code (gui/colorPicker.js) never imports this module.
export { isGradientValue, splitBackgroundValue };

const HEX6 = /^#?[0-9a-fA-F]{6}$/;

export function articleTextColor({ backgroundColor, forgroundColor, forgroundImageColor } = {}) {
    if (forgroundImageColor) {
        return forgroundImageColor;
    }
    if (forgroundColor) {
        return forgroundColor;
    }
    const bg = (backgroundColor || '').trim();
    if (HEX6.test(bg)) {
        return util.isDark(bg) ? '#ffffff' : '#000000';
    }
    return 'inherit';
}

// Text can cross both light and dark areas in layered gradients and flags. Use
// the opposite luminance for a thin outline so either foreground remains
// legible without changing the foreground color chosen by the background.
export function articleTextOutlineColor(textColor) {
    const foreground = (textColor || '').trim();
    return HEX6.test(foreground) && util.isDark(foreground)
        ? 'rgba(255,255,255,0.72)'
        : 'rgba(0,0,0,0.72)';
}

// Single source of truth for rendering a post background. backgroundImage (admin
// form) wins over a gradient stored in background (composer picker), preserving
// the legacy stacking behavior in gui/listFrontpage.js.
export function articleBackgroundStyles({ background, backgroundImage } = {}) {
    const bgGradient = isGradientValue(background) ? splitBackgroundValue(background) : null;
    const [backgroundColor, forgroundColor] = !bgGradient && background ? background.split(',') : [];
    const [imageCss, forgroundImageColor] = backgroundImage ? backgroundImage.split(';') : [];
    const textColor = articleTextColor({
        backgroundColor,
        forgroundColor: bgGradient ? bgGradient.fg : forgroundColor,
        forgroundImageColor,
    });
    return {
        backgroundImageCss: imageCss || (bgGradient ? bgGradient.css : undefined),
        backgroundColor: backgroundColor || undefined,
        textColor,
        textOutlineColor: articleTextOutlineColor(textColor),
    };
}
